/**
 * Single source of truth for the map's zoom-tier behaviour.
 *
 * The measure visualisation has exactly two tiers, separated at one breakpoint:
 *   zoom <  overviewMax : species-coloured overview line (one line per habitat)
 *   zoom >= overviewMax : per-measure colour via parallel line-offset "slots"
 *
 * Both the MapLibre layers (`Map.tsx`) and the sidebar legend (`Sidebar.tsx`)
 * import this so the two can never drift apart. The tier handoff is implemented
 * with layer-level `minzoom`/`maxzoom` (a hard cutoff), not two independently
 * tuned opacity ramps — that avoids the class of bug where both tiers fade to
 * near-zero at the same zoom and nothing renders.
 */
export const ZOOM_BREAKPOINTS = {
  /** Below this zoom: species overview. At/above: per-measure detail. */
  overviewMax: 12,
} as const;

/**
 * How many simultaneously-active measures on a single line are drawn as
 * distinct parallel offset lines. Real data tops out at 4 concurrent measures
 * per line; this leaves one slot of headroom. Measures beyond the cap are still
 * listed in the details panel — only their dedicated line is omitted.
 */
export const MAX_VISIBLE_MEASURE_SLOTS = 5;

/**
 * Perpendicular spacing (screen px) between adjacent measure slot lines.
 * Must stay wide enough that each slot's white halo (see HALO_EXTRA_WIDTH_PX
 * in Map.tsx) doesn't fully overlap its neighbour's — otherwise the halos
 * merge into one blob instead of showing a visible gap between lines.
 */
export const SLOT_SPACING_PX = 8;
